import {
  IdentificationCard,
  LinkedinLogo,
  MapPin,
} from "@phosphor-icons/react";
import { useMemo } from "react";

import { DataTable } from "common/components/DataTable";
import useDataTable from "common/components/DataTable/useDataTable";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import type { LinkedInProfile } from "common/types";

import type { ColumnDef } from "@tanstack/react-table";

interface SearchTableProps {
  profiles: LinkedInProfile[];
  isLoading: boolean;
  totalItemCount: number;
}

export default function SearchTable({
  profiles,
  isLoading,
  totalItemCount,
}: SearchTableProps) {
  const columns = useMemo<ColumnDef<LinkedInProfile>[]>(
    () => [
      {
        id: "lead",
        header: "Leads",
        cell: ({ row: { original } }) => {
          if (isLoading) {
            return (
              <div className="flex items-center gap-x-2.5">
                <Skeleton className="size-8 rounded-full" />
                <Skeleton className="h-4 w-28" />
              </div>
            );
          }

          const { picture_url, full_name, premium } = original;

          return (
            <div className="flex items-center gap-x-2.5">
              <ProfileImage src={picture_url} size="sm" />
              <span className="line-clamp-1 break-all text-xs-bold">
                {full_name}
              </span>
              {premium && (
                <LinkedinLogo
                  className="size-4 text-yellow-500"
                  weight="fill"
                />
              )}
            </div>
          );
        },
        enableHiding: false,
      },
      {
        id: "occupation",
        header: "Occupation",
        cell: ({ row: { original } }) =>
          isLoading ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <span className="line-clamp-1 break-all">
              {original.occupation}
            </span>
          ),
        meta: { icon: IdentificationCard, className: "min-w-40 max-w-40" },
      },
      {
        id: "location",
        header: "Location",
        cell: ({ row: { original } }) =>
          isLoading ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <span className="line-clamp-1 break-all">
              {original.location?.name}
            </span>
          ),
        meta: { icon: MapPin, className: "min-w-40 max-w-40" },
      },
    ],
    [isLoading],
  );

  // profiles are only previewed here, so no selection is passed
  const table = useDataTable({
    data: profiles || new Array(20).fill({}),
    columns,
  });

  return (
    <DataTable
      table={table}
      totalItemCount={totalItemCount}
      isLoading={isLoading}
    />
  );
}
